import {
  Box,
  Button,
  CloseButton,
  Divider,
  Heading,
  Flex,
  Tooltip,
  Truncated,
} from '@customerio/pluma-components/react';
import type { IconName } from '@customerio/pluma-icons';
import type { ReactNode } from 'react';
import * as styles from './index.css';
import * as headerTitleStyles from './header-title-adjacent.css';

export interface SidebarHeaderProps {
  title: ReactNode;
  /** Shown after the title (e.g. badges). Omit when unused so flex gap does not reserve space. */
  titleSuffix?: ReactNode;
  leadingActions?: ReactNode;
  trailingActions?: ReactNode;
  onClose: () => void;
  /**
   * Swaps the default X for another icon with a tooltip, for sidebars where
   * closing reads as putting the panel away rather than ending something.
   */
  closeControl?: SidebarCloseControl;
}

export type SidebarCloseControl = {
  icon: IconName;
  label: string;
};

export function SidebarHeader({
  title,
  titleSuffix,
  leadingActions,
  trailingActions,
  onClose,
  closeControl,
}: SidebarHeaderProps) {
  return (
    <>
      <Flex alignItems="center" justifyContent="space-between" p="100" pl="200" className={styles.sidebarHeader}>
        <Flex alignItems="center" gap="100" minW="0" flex={1}>
          {leadingActions}
          {titleSuffix != null ? (
            <Flex alignItems="center" gap="100" minW="0" flex={1}>
              <Box className={headerTitleStyles.headerTitleAdjacentToSuffix}>
                <Truncated as={Heading} level="4">
                  {title}
                </Truncated>
              </Box>
              {titleSuffix}
            </Flex>
          ) : (
            <Truncated as={Heading} level="4">
              {title}
            </Truncated>
          )}
        </Flex>
        <Flex alignItems="center" gap="100" flexShrink="0">
          {trailingActions}
          {closeControl != null ? (
            <Tooltip content={closeControl.label} isCustom={true}>
              {(context) => (
                <Button
                  variant="tertiary"
                  size="sm"
                  icon={closeControl.icon}
                  isIconOnly={true}
                  aria-label={closeControl.label}
                  onClick={onClose}
                  ref={context.reference.setReference}
                  {...context.reference.getReferenceProps()}
                />
              )}
            </Tooltip>
          ) : (
            <CloseButton onClick={onClose} />
          )}
        </Flex>
      </Flex>
      <Divider />
    </>
  );
}
